import Model from '../../ModelBinding/Model';
import MissionCollection from './MissionCollection';
import IconCamera from 'ocus-styleguide/src/assets/svg/camera.svg';
import IconVideo from 'ocus-styleguide/src/assets/svg/video.svg';
import IconDrone from 'ocus-styleguide/src/assets/svg/drone.svg';
import Icon360 from 'ocus-styleguide/src/assets/svg/360.svg';
import { formatCurrency } from '../../Helpers/FormatNumber';
import Organization from '../Organization/Organization';
import Address from '../Address/Address';
import Order from '../Order/Order';
import OfferCollection from '../Offer/OfferCollection';
import TagCollection from '../Tag/TagCollection';
import AvailabilityCollection from '../Availability/AvailabilityCollection';
import MissionMediaCollection from '../MissionMedia/MissionMediaCollection';
import Member from '../Member/Member';
import MemberCollection from '../Member/MemberCollection';
import dayjs from 'dayjs';
import ContactCollection from '../Contact/ContactCollection';
import MissionEventCollection from '../MissionEvent/MissionEventCollection';
import RateCollection from '../Rate/RateCollection';
import MissionStatus from '../MissionStatus/MissionStatus';
import StateCollection from '../State/StateCollection';
import MissionDocumentCollection from '../MissionDocument/MissionDocumentCollection';
import MissionEvaluationCollection from '../MissionEvaluation/MissionEvaluationCollection';
import Criticality from '../Criticality/Criticality';
import OrderLineItemCollection from '../OrderLineItem/OrderLineItemCollection';
import ArchivesCollection from '../Archives/ArchivesCollection';
import { QueryPartElement } from '../../ModelBinding/types';
import MissionQcRate from '../MissionQcRate/MissionQcRate';
import Bonus from '../Bonus/Bonus';

export default class Mission extends Model {
  public short_id?: string;
  public name?: string;
  public current_status?: string;
  public approved_appointment?: dayjs.Dayjs;
  public internal_reference?: string;
  public mission_type?: string[];
  public product_types?: string[];
  public description?: string;
  public selected_at?: dayjs.Dayjs;
  public expires_in?: number;
  public expected_total_media?: number;
  public created_by?: Member;
  public started_at?: dayjs.Dayjs;
  public created_at?: dayjs.Dayjs;
  public ordered_at?: dayjs.Dayjs;
  public updated_at?: dayjs.Dayjs;
  public organization?: Organization;
  public archives?: ArchivesCollection;
  public address?: Address;
  public order?: Order;
  public offers?: OfferCollection;
  public tags?: TagCollection;
  public contacts?: ContactCollection;
  public recipients?: MemberCollection;
  public availabilities?: AvailabilityCollection;
  public media?: MissionMediaCollection;
  public operator?: Member;
  public events?: MissionEventCollection;
  public type?: string;
  public status?: MissionStatus;
  public rates?:RateCollection;
  public states?:StateCollection;
  public mission_documents?:MissionDocumentCollection;
  public evaluations?:MissionEvaluationCollection;
  public rating?: number;
  public customer_rate?: number;
  public criticality?:Criticality;
  public visible?: boolean;
  public order_items?: OrderLineItemCollection;
  public cloned_to?: Mission;
  public cloned_from?: Mission;
  public reference_id?: string;
  public rated_at?: dayjs.Dayjs;
  public media_stats?: { [key: string]: number }|null;
  public public_status?: string;
  public organization_name?: string;
  public qc_rate?: MissionQcRate;
  public authorized_appointment_dates?: Array<string>;
  public selection_limits?: { [key: string]: number }|null;
  public ic_bonus?: Bonus;
  public ic_bonus_limit_date?: string;
  public appointment_change_blocked?: boolean;
  public v3Id?: string;

  bind() {
    this.short_id = this.getStringAttribute('short_id');
    this.name = this.getStringAttribute('name');
    this.type = this.getType();
    this.current_status = this.getStringAttribute('current_status');
    this.approved_appointment = this.getDateAttribute('approved_appointment');
    this.internal_reference = this.getStringAttribute('internal_reference');
    this.mission_type = this.getOriginalAttribute('mission_type');
    this.product_types = this.getOriginalAttribute('product_types');
    this.description = this.getStringAttribute('description');
    this.selected_at = this.getDateAttribute('selected_at');
    this.expires_in = this.getIntAttribute('expires_in');
    this.expected_total_media = this.getIntAttribute('expected_total_media');
    this.created_by = this.getRelationAttribute('created_by', Member) as Member;
    this.started_at = this.getDateAttribute('started_at');
    this.created_at = this.getDateAttribute('created_at');
    this.ordered_at = this.getDateAttribute('ordered_at');
    this.updated_at = this.getDateAttribute('updated_at');
    this.organization = this.getRelation('organization', Organization) as Organization | undefined;
    // tslint:disable-next-line:max-line-length
    this.archives = this.getRelationCollection('archives', ArchivesCollection) as ArchivesCollection | undefined;
    this.address = this.getRelation('address', Address) as Address | undefined;
    this.order = this.getRelation('order', Order) as Order | undefined;
    this.offers = this.getRelationCollection('offers', OfferCollection) as OfferCollection;
    this.tags = this.getRelationCollection('tags', TagCollection) as TagCollection;
    this.contacts = this.getRelationCollection('contacts', ContactCollection) as ContactCollection;
    this.recipients = this.getRelationCollection('recipients', MemberCollection) as MemberCollection;
    this.rates = this.getRelationCollection('rates', RateCollection) as RateCollection;
    this.appointment_change_blocked = this.getOriginalAttribute('appointment_change_blocked');
    this.availabilities = this.getRelationCollection(
      'availabilities',
      AvailabilityCollection,
    ) as AvailabilityCollection;
    this.media = this.getRelationCollection(
      'media',
      MissionMediaCollection,
    ) as MissionMediaCollection;
    this.operator = this.getRelationAttribute('operator', Member) as Member;
    this.events = this.getRelationCollection(
      'events',
      MissionEventCollection,
    ) as MissionEventCollection;
    this.status = this.getRelation('status', MissionStatus) as MissionStatus;
    this.states = this.getRelationCollection(
      'states',
      StateCollection,
    ) as StateCollection;
    this.mission_documents = this.getRelationCollection(
      'mission_documents',
      MissionDocumentCollection,
    ) as MissionDocumentCollection;
    this.evaluations = this.getRelationCollection(
      'evaluations',
      MissionEvaluationCollection,
    ) as MissionDocumentCollection;
    this.rating = this.getFloatAttribute('rate');
    this.customer_rate = this.getFloatAttribute('customer_rate');
    this.criticality = this.getRelation('criticality', Criticality) as Criticality;
    this.visible = this.getBooleanAttribute('visible');
    this.order_items = this.getRelationCollection(
      'order_items',
      OrderLineItemCollection,
    ) as OrderLineItemCollection;
    this.cloned_from = this.getRelationAttribute('cloned_from', Mission) as Mission;
    this.cloned_to = this.getRelationAttribute('cloned_to', Mission) as Mission;
    this.reference_id = this.getStringAttribute('reference_id');
    this.rated_at = this.getDateAttribute('rated_at');
    this.media_stats = this.getOriginalAttribute('media_stats') as { [key: string]: number } | null;
    this.public_status = this.getStringAttribute('public_status');
    this.organization_name = this.getStringAttribute('organization_name');
    this.qc_rate = this.getRelation(
      'qc_rate',
      MissionQcRate,
    ) as MissionQcRate;
    this.authorized_appointment_dates = this.getOriginalAttribute('authorized_appointment_dates');
    this.selection_limits = this.getOriginalAttribute('selection_limits') as { [key: string]: number } | null;
    this.ic_bonus = this.getRelation('ic_bonus', Bonus);
    this.ic_bonus_limit_date = this.getOriginalAttribute('ic_bonus_limit_date');
    this.v3Id = this.getOriginalAttribute('v3_id');
  }

  static getCollection() {
    return MissionCollection;
  }

  getLocalizedApprovedAppointment(options: Intl.DateTimeFormatOptions = {}) {
    if (!this.approved_appointment) return '';

    const locale = (window as any).$nuxt.$LanguageService?.currentLanguage?.replace('_', '-');

    return (new Intl.DateTimeFormat(locale, {
      ...(this.address?.timezone ? { timeZone: this.address.timezone } : {}),
      ...options,
    })).format(this.approved_appointment.toDate());
  }

  getDayBox() {
    return this.getLocalizedApprovedAppointment({
      weekday: 'short',
    });
  }

  getDateBox() {
    return this.getLocalizedApprovedAppointment({
      month: 'short',
      day: 'numeric',
    });
  }

  getFullDateBox() {
    return this.getLocalizedApprovedAppointment({
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    });
  }

  getHourBox() {
    if (!this.approved_appointment) return 'RDV';
    return this.getLocalizedApprovedAppointment({
      hour: 'numeric',
      minute: 'numeric',
    });
  }

  getInfoBox() {
    return this.approved_appointment
      ? this.approved_appointment.from(new Date())
      : 'Make an appointment !';
  }

  getColorBox() {
    if (this.$config.ocusDashboard === 'operator') {
      const color = {
        availableMission: '#56CCF2',
        inCart: '#FFCC00',
        myMission: '#FF8A1F',
      };
      switch (this.current_status){
        case 'CREATED':
        case 'ACTIVATED':
        case 'WAITING_ATTRIBUTION':
          if (this.selected_at) {
            return color.inCart;
          }
          return color.availableMission;
        case 'APPOINTED':
        case 'ARCHIVED':
        case 'ATTRIBUTED':
        case 'CANCELED':
        case 'REFUSED':
        case 'MEDIA_TREATED':
        case 'WAITING_APPOINTMENT':
        case 'WAITING_MEDIA_TREATMENT':
        default:
          return color.myMission;
      }
    }

    if (!this.product_types?.length || this.product_types?.length > 1) return '#FFCD00';

    switch (this.product_types[0]) {
      case 'DRONE':
        return '#86DC84';
      case 'VIDEO':
      case 'TIMELAPSE':
        return '#CF10FF';
      case '360':
      case 'MAP':
      case 'MATTERPORT':
        return '#00C1C8';
      case 'PHOTO':
      default:
        return '#FFCD00';
    }
  }

  getIconBox() {
    if (!this.product_types?.length || this.product_types?.length > 1) return null;
    switch (this.product_types[0]) {
      case 'DRONE':
        return IconDrone;
      case 'VIDEO':
      case 'TIMELAPSE':
        return IconVideo;
      case '360':
      case 'MAP':
      case 'MATTERPORT':
        return Icon360;
      case 'PHOTO':
        return IconCamera;
      default:
        return null;
    }
  }

  get displayOffer() {
    return this.offers ? this.offers.map(offer => offer.name).join(', ') : undefined;
  }

  get currency() {
    if (this.order_items?.length) {
      return this.order_items.getItem(0)?.currency || 'EUR';
    }
    return this.offers?.getItem(0)?.currency || 'EUR';
  }

  get price() {
    if (this.order_items?.length) {
      const orderItemsPrice = this.order_items
        .filter(({ order_item_type, draft }) => (order_item_type === 'customer' && !draft))
        .reduce((total: number, order_item) => total + (order_item.price || 0), 0);
      return Math.max(orderItemsPrice, 0);
    }
    if (this.offers?.length) {
      const offersPrice = this.offers
        .reduce((total: number, offer) => total + (offer.price || 0), 0);
      return Math.max(offersPrice, 0);
    }
    return 0;
  }

  get displayPrice() {
    return formatCurrency(this.price / 100, this.currency);
  }

  get operator_currency() {
    return this.offers?.getItem(0)?.operator_currency || 'EUR';
  }

  get rate() {
    if (this.order_items?.length) {
      const orderItemsPrice = this.order_items
        .filter(({ order_item_type }) => (order_item_type === 'operator'))
        .reduce((total: number, order_item) => total + (order_item.price || 0), 0);
      return Math.max(orderItemsPrice, 0);
    }
    if (this.offers?.length) {
      const offersPrice = this.offers
        .reduce((total: number, { operator_rate }) => total + (operator_rate || 0), 0);
      return Math.max(offersPrice, 0);
    }
    return 0;
  }

  get displayRate() {
    if (!this.offers) return '';
    return formatCurrency(this.rate / 100, this.operator_currency);
  }

  static getFinishedStatuses() {
    return [
      'ARCHIVED',
      'MEDIA_TREATED',
    ];
  }

  static getCanceledStatuses() {
    return [
      'CANCELED',
      'UNASSIGNED',
    ];
  }

  static getBlockers() {
    return [
      { code: 'SP:GUIDELINES', name: 'GUIDELINES CLARIFICATION REQUEST' },
      { code: 'SP:CALL_ISSUE', name: 'CALL ISSUE' },
      { code: 'SP:MISSING_INFO', name: 'INFORMATION REQUEST' },
      { code: 'SP:UPLOAD_ISSUE', name: 'UPLOAD ISSUE' },
      { code: 'SP:REFUSAL', name: 'CONTACT REFUSAL' },
      { code: 'SP:SET_DATE_LATER', name: 'SET DATE LATER' },
    ];
  }

  static getEvents() {
    return [
      'WA:WAITING_ATTRIBUTION',
      'WA:VALIDATE_UPLOAD',
      'WA:UNASSIGN',
      'WA:SELECTION',
      'WA:MEDIA_TREATED',
      'WA:GUIDELINE',
      'WA:CONTACT_CALL_CONFIRMATION',
      'WA:ATTRIBUTION',
      'WA:ACTIVATION',
    ];
  }
}
